import { useMutation, useQueryClient } from '@tanstack/react-query';
import { type SavedViewsListResult, deleteSavedView } from '../lib/saved-views-client.js';
import { savedViewsQueryKey } from './use-saved-views.js';

interface UseDeleteSavedViewOptions {
  tenantReportId: string;
}

export function useDeleteSavedView({ tenantReportId }: UseDeleteSavedViewOptions) {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (viewId: string) => deleteSavedView(viewId),
    onMutate: async (viewId: string) => {
      // Optimistic update: remove the view from the list immediately
      await queryClient.cancelQueries({ queryKey: savedViewsQueryKey(tenantReportId) });
      const previous = queryClient.getQueryData<SavedViewsListResult>(
        savedViewsQueryKey(tenantReportId),
      );
      if (previous !== undefined) {
        const next: SavedViewsListResult = {
          items: previous.items.filter((v) => v.id !== viewId),
          total: previous.total - 1,
        };
        queryClient.setQueryData(savedViewsQueryKey(tenantReportId), next);
      }
      return { previous };
    },
    onError: (_err, _viewId, context) => {
      // Rollback on error
      const ctx = context as { previous?: SavedViewsListResult } | undefined;
      if (ctx?.previous !== undefined) {
        queryClient.setQueryData(savedViewsQueryKey(tenantReportId), ctx.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: savedViewsQueryKey(tenantReportId) });
    },
  });
}
